/**
 * `/` — landing page after sign-in. Lightweight "your account at a
 * glance" — pulls counts from the three primary read-models the
 * customer cares about (subscriptions / connections / invoices) so the
 * page isn't a stub. Each row links to the section that owns the
 * detail.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { fmtNumber } from '../lib/format-money';

interface Counts {
  activeSubscriptions: number | null;
  activeConnections: number | null;
  totalInvoices: number | null;
}

/** OCP-627 - the durable AI-gate notice: which workspaces are paused and why. */
interface GateNotice {
  workspaces: Array<{ tenant_id: string; subdomain: string; custom_domain: string | null; gated: boolean; blocked_reason: string | null }>;
}

export function OverviewPage() {
  const { t } = useTranslation('overview');
  const { user } = useAuth();
  const [counts, setCounts] = useState<Counts>({
    activeSubscriptions: null,
    activeConnections: null,
    totalInvoices: null,
  });
  const [gated, setGated] = useState<GateNotice['workspaces']>([]);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      const safe = async <T,>(path: string): Promise<T | null> => {
        try {
          return await api<T>(path, { signal: ac.signal });
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return null;
          if (err instanceof ApiError) return null;
          return null;
        }
      };
      const [subs, conns, invs, ai] = await Promise.all([
        safe<{ items: { status?: string }[] }>('/v1/portal/me/subscriptions'),
        safe<{ items: { status?: string }[] }>('/v1/portal/mail/connections'),
        safe<{ items: unknown[] }>('/v1/portal/billing/invoices'),
        safe<GateNotice>('/v1/portal/ai/usage?period=7d'),
      ]);
      if (ac.signal.aborted) return;
      setGated(ai ? ai.workspaces.filter((w) => w.gated) : []);
      setCounts({
        activeSubscriptions: subs
          ? subs.items.filter((s) => s.status === 'active').length
          : null,
        activeConnections: conns
          ? conns.items.filter((c) => c.status === 'active').length
          : null,
        totalInvoices: invs ? invs.items.length : null,
      });
    })();
    return () => ac.abort();
  }, []);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">{t('welcome')}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t('signedInAs')}
          <span className="font-medium text-gray-900 dark:text-gray-100">{user?.email ?? t('you')}</span>.
        </p>
      </div>

      {/* OCP-627 - durable notice: survives the mail, sits where the customer lands. */}
      {gated.length > 0 && (
        <div
          role="alert"
          data-testid="overview-ai-gate-notice"
          className="mb-8 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-800 dark:text-red-300"
        >
          <p className="font-semibold">{t('aiPaused', { count: gated.length })}</p>
          <p className="mt-1">
            {gated.map((w) => w.custom_domain ?? `${w.subdomain}.orbo.to`).join(', ')}:{' '}
            {gated.every((w) => w.blocked_reason === 'allowance_exhausted')
              ? t('aiAllowanceExhausted')
              : gated.every((w) => w.blocked_reason === 'overage_cap_reached')
                ? t('aiOverageCap')
                : t('aiOther')}
          </p>
          <Link to="/ai" className="mt-2 inline-block rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700">
            {t('openAi')}
          </Link>
        </div>
      )}

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat
          label={t('stat.activeSubscriptions')}
          value={counts.activeSubscriptions}
          to="/subscriptions"
        />
        <Stat
          label={t('stat.activeConnections')}
          value={counts.activeConnections}
          to="/mail/connections"
        />
        <Stat
          label={t('stat.invoices')}
          value={counts.totalInvoices}
          to="/billing"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card
          title={t('card.subscriptions')}
          hint={t('card.subscriptionsHint')}
          to="/subscriptions"
        />
        <Card title={t('card.mail')} hint={t('card.mailHint')} to="/mail" />
        <Card title={t('card.licenses')} hint={t('card.licensesHint')} to="/licenses" />
        <Card title={t('card.billing')} hint={t('card.billingHint')} to="/billing" />
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  to,
}: {
  label: string;
  value: number | null;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3 shadow-sm transition hover:border-brand-300 hover:shadow"
    >
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
        {value === null ? '—' : fmtNumber(value)}
      </div>
    </Link>
  );
}

function Card({ title, hint, to }: { title: string; hint: string; to: string }) {
  const { t } = useTranslation('overview');
  return (
    <Link
      to={to}
      className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm transition hover:border-brand-300 hover:shadow"
    >
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</div>
      <div className="mt-1 text-xs text-gray-500">{hint}</div>
      <div className="mt-3 text-xs font-medium text-brand-500">{t('card.open')}</div>
    </Link>
  );
}
